import isProblemDetails from '../lib/isProblemDetails';
import ProblemDetailsError from './ProblemDetailsError';
import {ProblemDetails, ServiceError} from '../types';

/**
 * Normalizes the `errors` payload of an error response to the codebase's
 * ServiceError[] shape. ASP.NET model validation and FastEndpoints binding
 * failures send a dictionary ({ field: ["message", ...] }) instead of the
 * ServiceResult error list — without normalization that shape crashes any
 * component that iterates problemDetails.errors.
 */
const normalizeErrors = (errors: unknown): ServiceError[] | undefined => {
    if (errors === null || errors === undefined) {
        return undefined;
    }

    if (Array.isArray(errors)) {
        return errors as ServiceError[];
    }

    if (typeof errors === 'object') {
        return Object.entries(errors as Record<string, unknown>).flatMap(
            ([field, messages]) =>
                (Array.isArray(messages) ? messages : [messages]).map(
                    message => ({
                        message:
                            field && field !== 'generalErrors'
                                ? `${field}: ${String(message)}`
                                : String(message)
                    })
                )
        );
    }

    return [{message: String(errors)}];
};

export const handleResponseError = async (response: Response) => {
    const statusCode = response.status;
    const contentType = response.headers.get('content-type') || '';

    // Create a base problem details object
    const problemDetails: ProblemDetails = {
        type: 'https://tools.ietf.org/html/rfc7231',
        title: getDefaultErrorMessage(statusCode),
        status: statusCode,
        detail: null,
        instance: response.url || null,
        reasonPhrase: response.statusText || null
    };

    // Handle different content types
    if (
        contentType.includes('application/json') ||
        contentType.includes('application/problem+json')
    ) {
        try {
            const errorData = await response.json();

            // If response is already a problem details object, use it
            if (isProblemDetails(errorData)) {
                throw new ProblemDetailsError(errorData);
            }

            // Otherwise, enhance our problem details with any information from the response
            if (errorData.message) {
                problemDetails.detail = errorData.message;
            } else if (typeof errorData.error === 'string') {
                problemDetails.detail = errorData.error;
            }

            // Add any additional properties from errorData to the problem details
            Object.entries(errorData).forEach(([key, value]) => {
                if (
                    !['type', 'title', 'status', 'detail', 'instance'].includes(
                        key
                    )
                ) {
                    // Validation-failure responses carry errors as a dictionary,
                    // not the ServiceError list the UI renders — normalize.
                    problemDetails[key] =
                        key === 'errors' ? normalizeErrors(value) : value;
                }
            });
        } catch (error) {
            if (error instanceof ProblemDetailsError) {
                throw error; // Re-throw ProblemDetailsError immediately
            }
            // On parsing error, try to get text content
            const textContent = await response.clone().text();
            problemDetails.detail = textContent || problemDetails.title;
        }
    } else if (contentType.includes('text/html')) {
        // Handle HTML content
        const htmlContent = await response.text();
        problemDetails.detail =
            'The server returned an error response. Click [View Response] for details';
        problemDetails.html = htmlContent; // Add HTML content to the additional properties
    } else {
        // Handle text or other content types
        const textContent = await response.text();
        problemDetails.detail = textContent || problemDetails.title;
    }

    throw new ProblemDetailsError(problemDetails);
};

/**
 * Returns a default error message based on the HTTP status code
 */
function getDefaultErrorMessage(statusCode: number): string {
    switch (statusCode) {
        case 400:
            return 'Bad request.';
        case 401:
            return 'Unauthorized.';
        case 403:
            return 'Forbidden.';
        case 404:
            return 'Not found.';
        case 405:
            return 'Method not allowed.';
        case 408:
            return 'Request timeout.';
        case 409:
            return 'Conflict.';
        case 413:
            return 'Payload too large. Please reduce the size of your request.';
        case 429:
            return 'Too many requests. Please try again later.';
        case 500:
            return 'Internal server error. Please try again later.';
        case 502:
            return 'Bad gateway.';
        case 503:
            return 'Service unavailable. Please try again later.';
        case 504:
            return 'Gateway timeout.';
        case 524:
            return 'A timeout occurred.';
        default:
            return `HTTP Error ${statusCode}`;
    }
}
